import { useState, useEffect, useLayoutEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Check, ChevronsUpDown, ArrowLeft, ArrowRight, Plus, Minus } from "lucide-react";
import { cn } from "@/lib/utils";
import { apiUrl } from "@/lib/api-client";
import { enqueueCrmSyncOp } from "@/lib/crm-sync-outbox";
import { notify } from "@/lib/notify";
import {
  saveMeasurementWizardDraft,
  loadMeasurementWizardDraft,
  deleteMeasurementWizardDraft,
  type MeasurementWizardDraft,
} from "@/lib/measurement-wizard-draft";
import { saveMeasureDealsSnapshot, loadMeasureDealsSnapshot } from "@/lib/measure-deals-cache";
import { saveProductTypesSnapshot, loadProductTypesSnapshot } from "@/lib/product-types-cache";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "react-i18next";
import { StepIndicator } from "./StepIndicator";
import { GateMeasurementForm } from "./GateMeasurementForm";
import { ProductItemForm } from "./ProductItemForm";
import { PhotoCheckStep } from "./PhotoCheckStep";
import { SummaryStep } from "./SummaryStep";
import type { GateMeasurement, MeasurementData, DealProductItem, ProductType } from "@shared/api";

interface MeasurementWizardProps {
  dealId: string | null;
  onDealSelect?: (dealId: string) => void;
  onComplete?: (localMeasurementData: MeasurementData) => void;
  onCreateDeal?: () => void;
  /** When true, wizard opens on PRODUCT_TYPES step (used after creating a new deal from Measure). */
  initialStepFromCreate?: boolean;
}

const STEPS = {
  DEAL_SELECT: 1,
  PRODUCT_TYPES: 2,
  COUNT_PER_TYPE: 3,
  /** Első termék adat lépés; termék k adat = 4+2*k, termék k fotó = 5+2*k, összefoglaló = 4+2*n */
  FIRST_PRODUCT: 4,
};

const STEP_KEYS_PRODUCT = [
  "measurementExtra.stepSelectDeal",
  "measurementExtra.stepProductTypes",
  "measurementExtra.stepCount",
  "measurementExtra.stepProductData",
  "measurementExtra.stepPhotoCheck",
  "measurementExtra.stepSummary",
];

const STEPS_LEGACY = {
  DEAL_SELECT: 1,
  GATE_COUNT: 2,
  GATE_DATA: 3,
  PHOTO_CHECK: 4,
  SUMMARY: 5,
};

const STEP_KEYS_LEGACY = [
  "measurementExtra.stepSelectDeal",
  "measurementExtra.stepGateCount",
  "measurementExtra.stepGateData",
  "measurementExtra.stepPhotoCheck",
  "measurementExtra.stepSummary",
];

export function MeasurementWizard({
  dealId: initialDealId,
  onDealSelect,
  onComplete,
  onCreateDeal,
  initialStepFromCreate,
}: MeasurementWizardProps) {
  const [dealId, setDealId] = useState<string | null>(initialDealId);
  // When opened from Deal FAB with /crm/measure/:id, skip deal select entirely (start at step 2 = PRODUCT_TYPES or GATE_COUNT)
  const [currentStep, setCurrentStep] = useState(() => (initialDealId ? 2 : 1));
  const [selectedProductTypes, setSelectedProductTypes] = useState<string[]>([]);
  const [countByType, setCountByType] = useState<Record<string, number>>({});
  const [products, setProducts] = useState<DealProductItem[]>([]);
  const [gateCount, setGateCount] = useState(1);
  const [currentGateIndex, setCurrentGateIndex] = useState(0);
  const [gates, setGates] = useState<GateMeasurement[]>([]);
  const [dealSelectOpen, setDealSelectOpen] = useState(false);
  const [dealSearchValue, setDealSearchValue] = useState("");

  const qc = useQueryClient();
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();

  const deal = useQuery({
    queryKey: ["deal", dealId],
    enabled: !!dealId,
    queryFn: async () => {
      try {
        const r = await fetch(apiUrl(`/api/crm/deals/${dealId}`), { credentials: "include" });
        if (!r.ok) throw new Error(`Deal fetch error: ${r.status}`);
        return r.json();
      } catch (err) {
        console.warn("Deal query error:", err instanceof Error ? err.message : err);
        return {};
      }
    },
  });

  const measureDeals = useQuery({
    queryKey: ["measure-deals", i18n.language],
    queryFn: async () => {
      try {
        const r = await fetch(apiUrl("/api/crm/deals/measure"), { credentials: "include" });
        if (!r.ok) throw new Error(`Measure deals fetch error: ${r.status}`);
        const j = await r.json();
        const groups = (j.groups ?? [{ label: t("handover.dealGroupOther"), deals: j.deals ?? [] }]) as { label: string; deals: any[] }[];
        saveMeasureDealsSnapshot(groups);
        return groups;
      } catch (err) {
        console.error("Measure deals query error:", err instanceof Error ? err.message : err);
        const cached = loadMeasureDealsSnapshot();
        if (cached) {
          if (cached.stale) {
            notify.info(t("measure.cachedDealList"), t("measure.cachedDealListDesc"));
          }
          return cached.data;
        }
        return [];
      }
    },
  });

  const productTypesLang = (i18n.language === "sk" ? "sk" : i18n.language === "en" ? "en" : "hu") as "hu" | "sk" | "en";
  const productTypesQuery = useQuery({
    queryKey: ["product-types", productTypesLang],
    queryFn: async () => {
      try {
        const r = await fetch(apiUrl(`/api/crm/product-types?lang=${productTypesLang}`), { credentials: "include" });
        if (!r.ok) throw new Error(`Product types fetch error: ${r.status}`);
        const j = await r.json();
        const list = (j.productTypes || []) as ProductType[];
        saveProductTypesSnapshot(productTypesLang, list);
        return list;
      } catch (err) {
        console.warn("Product types query error:", err instanceof Error ? err.message : err);
        return loadProductTypesSnapshot(productTypesLang) ?? [];
      }
    },
  });

  const productTypes = productTypesQuery.data || [];

  /** Offline: GET deal fails; title still shown from cached measure-deals list. */
  const dealTitleFromMeasureList = useMemo(() => {
    if (!dealId || !measureDeals.data?.length) return null;
    for (const g of measureDeals.data) {
      const d = g.deals.find((x: { id?: string }) => x.id === dealId);
      if (d && (d.title != null || (d as { name?: string }).name != null)) {
        return String((d as { title?: string; name?: string }).title ?? (d as { name?: string }).name ?? "").trim() || null;
      }
    }
    return null;
  }, [dealId, measureDeals.data]);
  const useProductFlow = productTypes.length > 0;
  const navigate = useNavigate();
  const location = useLocation();

  // --- Draft restore: load persisted wizard state from IDB on mount ---
  const [draftRestored, setDraftRestored] = useState(false);
  useEffect(() => {
    if (!dealId || draftRestored) return;
    let cancelled = false;
    void loadMeasurementWizardDraft(dealId).then((draft) => {
      if (cancelled || !draft) {
        setDraftRestored(true);
        return;
      }
      if (draft.products.length > 0) setProducts(draft.products);
      if (draft.gates.length > 0) {
        setGates(draft.gates);
        setGateCount(draft.gateCount);
      }
      if (draft.selectedProductTypes.length > 0) setSelectedProductTypes(draft.selectedProductTypes);
      if (Object.keys(draft.countByType).length > 0) setCountByType(draft.countByType);
      setCurrentGateIndex(draft.currentGateIndex);
      if (draft.currentStep > 1) setCurrentStep(draft.currentStep);
      setDraftRestored(true);
    });
    return () => { cancelled = true; };
  }, [dealId]);

  // --- Draft save: debounced persist on state changes ---
  useEffect(() => {
    if (!dealId || !draftRestored) return;
    const timer = setTimeout(() => {
      const draft: MeasurementWizardDraft = {
        dealId,
        updatedAt: Date.now(),
        currentStep,
        useProductFlow,
        selectedProductTypes,
        countByType,
        products,
        gates,
        gateCount,
        currentGateIndex,
      };
      void saveMeasurementWizardDraft(draft);
    }, 500);
    return () => clearTimeout(timer);
  }, [dealId, draftRestored, currentStep, useProductFlow, selectedProductTypes, countByType, products, gates, gateCount, currentGateIndex]);

  useEffect(() => {
    if (initialStepFromCreate && dealId && useProductFlow) {
      setCurrentStep(STEPS.PRODUCT_TYPES);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [initialStepFromCreate, dealId, useProductFlow, navigate, location.pathname]);

  useEffect(() => {
    if (deal.data?.measurementData) {
      const data = deal.data.measurementData as MeasurementData;
      if (data.products && data.products.length > 0) {
        setProducts(data.products);
        const types = [...new Set(data.products.map((p) => p.productType))];
        setSelectedProductTypes(types);
        const counts: Record<string, number> = {};
        types.forEach((t) => {
          counts[t] = data.products!.filter((p) => p.productType === t).length;
        });
        setCountByType(counts);
      }
      if (data.gates && data.gates.length > 0) {
        setGates(data.gates);
        setGateCount(data.gates.length);
      }
    }
  }, [deal.data]);

  useEffect(() => {
    if (gateCount > 0 && !useProductFlow) {
      const newGates: GateMeasurement[] = [];
      for (let i = 0; i < gateCount; i++) {
        newGates.push(
          gates[i] || {
            id: `gate-${Date.now()}-${i}`,
            widths: [],
            heights: [],
            notes: "",
            surface: "",
            color: "",
            prelis: "",
          }
        );
      }
      setGates(newGates.slice(0, gateCount));
    }
  }, [gateCount, useProductFlow]);

  const saveMutation = useMutation({
    mutationFn: async (payload: Record<string, any>) => {
      try {
        const r = await fetch(apiUrl(`/api/crm/deals/${dealId}`), {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        });
        if (!r.ok) {
          await enqueueCrmSyncOp("deal_patch", dealId!, payload);
          notify.info(t("uploadOutbox.patchQueued"), t("uploadOutbox.patchQueuedDesc"));
          return null;
        }
        return r.json();
      } catch {
        await enqueueCrmSyncOp("deal_patch", dealId!, payload);
        notify.info(t("uploadOutbox.patchQueued"), t("uploadOutbox.patchQueuedDesc"));
        return null;
      }
    },
    onSuccess: async (result) => {
      if (!result) return;
      qc.invalidateQueries({ queryKey: ["deal", dealId] });
      const address = deal.data?.address;
      if (address && typeof address === "string" && address.trim()) {
        try {
          const r = await fetch(apiUrl("/api/crm/deals/check-duplicate"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ address: address.trim(), dealId }),
          });
          if (r.ok) {
            const { possibleDuplicates } = await r.json();
            if (possibleDuplicates?.length > 0) {
              toast({
                title: t("measurementExtra.possibleDuplicate"),
                description: t("measurementExtra.duplicateDealsByAddress", { count: possibleDuplicates.length }),
                variant: "default",
              });
            }
          }
        } catch {
          // ignore
        }
      }
    },
  });

  const flatProducts = useMemo(() => products, [products]);
  const currentProductIndex = useMemo(() => {
    if (!useProductFlow || currentStep < STEPS.FIRST_PRODUCT) return 0;
    const n = flatProducts.length;
    if (n === 0) return 0;
    const summaryStepVal = STEPS.FIRST_PRODUCT + 2 * n;
    if (currentStep >= summaryStepVal) return 0;
    return Math.floor((currentStep - STEPS.FIRST_PRODUCT) / 2);
  }, [useProductFlow, currentStep, flatProducts.length]);
  const currentProduct = flatProducts[currentProductIndex];
  const productTypeForCurrent = currentProduct
    ? productTypes.find((pt) => pt.name === currentProduct.productType)
    : null;

  useEffect(() => {
    if (!dealId) return;
    if (useProductFlow && flatProducts.length > 0) {
      const hasData = flatProducts.some(
        (p) =>
          (p.widths && p.widths.some((w) => w.trim() !== "")) ||
          (p.heights && p.heights?.some((h) => h.trim() !== "")) ||
          (p.surface && p.surface.trim() !== "") ||
          (p.notes && p.notes.trim() !== "") ||
          (p.type && p.type.trim() !== "") ||
          (p.power && p.power.trim() !== "")
      );
      if (!hasData) return;
      const timeoutId = setTimeout(() => {
        const measurementData: MeasurementData = {
          products: flatProducts,
          gates: [], // keep empty when using products
        };
        const first = flatProducts[0];
        const derived = first
          ? {
              width: first.widths?.[0]?.trim() || null,
              height: first.heights?.[0]?.trim() || null,
              color: first.color || null,
              surface: first.surface || null,
              emboss: first.prelis || null,
            }
          : {};
        saveMutation.mutate({ measurementData, ...derived });
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
    if (!useProductFlow && gates.length > 0) {
      const hasData = gates.some(
        (g) =>
          (g.widths.length > 0 && g.widths.some((w) => w.trim() !== "")) ||
          g.heights.some((h) => h.trim() !== "") ||
          g.surface.trim() !== "" ||
          g.notes.trim() !== ""
      );
      if (!hasData) return;
      const timeoutId = setTimeout(() => {
        const measurementData: MeasurementData = { gates };
        const primaryGate = gates[0];
        const derived = primaryGate
          ? {
              width: primaryGate.widths?.[0]?.trim() || null,
              height: primaryGate.heights?.[0]?.trim() || null,
              color: primaryGate.color || null,
              surface: primaryGate.surface || null,
              emboss: primaryGate.prelis || null,
            }
          : {};
        saveMutation.mutate({ measurementData, ...derived });
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [useProductFlow, flatProducts, gates, dealId]);

  const handleDealSelect = (id: string) => {
    setDealId(id);
    setDealSelectOpen(false);
    setDealSearchValue("");
    onDealSelect?.(id);
    // Do not auto-advance step; user must click "Next" when selecting from list.
  };

  const toggleProductType = (name: string) => {
    setSelectedProductTypes((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const handleCountChange = (typeName: string, count: number) => {
    setCountByType((prev) => ({ ...prev, [typeName]: Math.max(1, count) }));
  };

  const buildProductsFromCounts = () => {
    const list: DealProductItem[] = [];
    selectedProductTypes.forEach((typeName) => {
      const count = countByType[typeName] ?? 1;
      const pt = productTypes.find((p) => p.name === typeName);
      for (let i = 1; i <= count; i++) {
        const existing = products.find(
          (p) => p.productType === typeName && p.index === i
        );
        list.push(
          existing || {
            id: `prod-${Date.now()}-${typeName}-${i}`,
            productType: typeName,
            productTypeId: pt?.id,
            index: i,
            widths: [],
            heights: [],
            notes: "",
            surface: "",
            color: "",
            prelis: "",
          }
        );
      }
    });
    setProducts(list);
  };

  const handleProductChange = (item: DealProductItem) => {
    const next = [...flatProducts];
    next[currentProductIndex] = item;
    setProducts(next);
  };

  const hasDraftData = (d: any) => {
    const md = d?.measurementData ?? d?.measurement_data;
    if (!md) return false;
    if (md.completedAt) return false;
    return (md.products?.length > 0) || (md.gates?.length > 0);
  };

  const canProceedFromProductData = () => {
    if (!productTypeForCurrent) return true;
    const required = productTypeForCurrent.measurementFields?.filter((f) => f.required) || [];
    for (const f of required) {
      if (f.key === "widths" && (!currentProduct.widths || currentProduct.widths.filter((w) => w.trim()).length < 2))
        return false;
      if (f.key === "heights" && (!currentProduct.heights || currentProduct.heights.filter((h) => h.trim()).length < 2))
        return false;
      if (f.key === "surface" && !(currentProduct.surface?.trim())) return false;
      if (f.key === "notes" && !(currentProduct.notes?.trim())) return false;
      if (f.key === "type" && !(currentProduct.type?.trim())) return false;
      if (f.type === "boolean") {
        const v = ((currentProduct as unknown) as Record<string, unknown>)[f.key];
        if (v !== true && v !== false && v !== "true" && v !== "false") return false;
      }
    }
    return true;
  };

  const matchDealSearch = (deal: any) => {
    if (!dealSearchValue.trim()) return true;
    const searchLower = dealSearchValue.toLowerCase().trim();
    const title = (deal.title || "").toLowerCase();
    const id = (deal.id || "").toLowerCase();
    return (
      title.includes(searchLower) ||
      id.includes(searchLower) ||
      title.split(" ").some((word: string) => word.toLowerCase().includes(searchLower))
    );
  };

  const filteredGroups = useMemo(() => {
    const groups = measureDeals.data ?? [];
    return groups
      .map((g) => ({ label: g.label, deals: g.deals.filter(matchDealSearch) }))
      .filter((g) => g.deals.length > 0);
  }, [measureDeals.data, dealSearchValue]);

  const summaryStep = useProductFlow && flatProducts.length > 0 ? STEPS.FIRST_PRODUCT + 2 * flatProducts.length : 6;
  const totalSteps = useProductFlow ? (flatProducts.length > 0 ? summaryStep : 6) : 5;
  const productStepLabels = useMemo(() => {
    if (!useProductFlow || flatProducts.length === 0) return STEP_KEYS_PRODUCT.map((k) => t(k));
    const m = t("measurementExtra.shortMeasure");
    const ph = t("measurementExtra.shortPhoto");
    return [
      t("measurementExtra.stepSelectDeal"),
      t("measurementExtra.stepProductTypes"),
      t("measurementExtra.stepCount"),
      ...flatProducts.flatMap((p) => [
        t("measurementExtra.productDataBanner", { type: p.productType, index: p.index, phase: m }),
        t("measurementExtra.productDataBanner", { type: p.productType, index: p.index, phase: ph }),
      ]),
      t("measurementExtra.stepSummary"),
    ];
  }, [useProductFlow, flatProducts, t, i18n.language]);
  const stepKeys = useProductFlow ? productStepLabels : STEP_KEYS_LEGACY.map((k) => t(k));
  const stepLabels = useProductFlow ? productStepLabels : STEP_KEYS_LEGACY.map((k) => t(k));

  const isProductDataStep = (step: number) =>
    useProductFlow && step >= STEPS.FIRST_PRODUCT && (step - STEPS.FIRST_PRODUCT) % 2 === 0 && step < summaryStep;
  const isProductPhotoStep = (step: number) =>
    useProductFlow && step >= STEPS.FIRST_PRODUCT && (step - STEPS.FIRST_PRODUCT) % 2 === 1 && step < summaryStep;
  const productIndexFromStep = (step: number) => Math.floor((step - STEPS.FIRST_PRODUCT) / 2);

  const goNext = () => {
    if (useProductFlow) {
      if (currentStep === STEPS.DEAL_SELECT && dealId) setCurrentStep(STEPS.PRODUCT_TYPES);
      else if (currentStep === STEPS.PRODUCT_TYPES) setCurrentStep(STEPS.COUNT_PER_TYPE);
      else if (currentStep === STEPS.COUNT_PER_TYPE) {
        buildProductsFromCounts();
        setCurrentStep(STEPS.FIRST_PRODUCT);
      } else if (isProductDataStep(currentStep)) {
        setCurrentStep(currentStep + 1);
      } else if (isProductPhotoStep(currentStep)) {
        if (currentStep + 1 < summaryStep) setCurrentStep(currentStep + 1);
        else setCurrentStep(summaryStep);
      }
    } else {
      if (currentStep === STEPS_LEGACY.DEAL_SELECT && dealId) setCurrentStep(STEPS_LEGACY.GATE_COUNT);
      else if (currentStep === STEPS_LEGACY.GATE_COUNT) setCurrentStep(STEPS_LEGACY.GATE_DATA);
      else if (currentStep === STEPS_LEGACY.GATE_DATA) {
        if (currentGateIndex < gateCount - 1) setCurrentGateIndex(currentGateIndex + 1);
        else setCurrentStep(STEPS_LEGACY.PHOTO_CHECK);
      } else if (currentStep === STEPS_LEGACY.PHOTO_CHECK) setCurrentStep(STEPS_LEGACY.SUMMARY);
    }
  };

  const goPrev = () => {
    if (useProductFlow) {
      if (currentStep === STEPS.PRODUCT_TYPES) setCurrentStep(STEPS.DEAL_SELECT);
      else if (currentStep === STEPS.COUNT_PER_TYPE) setCurrentStep(STEPS.PRODUCT_TYPES);
      else if (isProductDataStep(currentStep)) {
        const k = productIndexFromStep(currentStep);
        if (k > 0) setCurrentStep(STEPS.FIRST_PRODUCT + 2 * k - 1);
        else setCurrentStep(STEPS.COUNT_PER_TYPE);
      } else if (isProductPhotoStep(currentStep)) {
        setCurrentStep(currentStep - 1);
      } else if (currentStep === summaryStep) {
        setCurrentStep(summaryStep - 1);
      } else setCurrentStep(currentStep - 1);
    } else {
      if (currentStep === STEPS_LEGACY.GATE_DATA && currentGateIndex > 0) {
        setCurrentGateIndex(currentGateIndex - 1);
      } else setCurrentStep(currentStep - 1);
    }
  };

  const isSummary = useProductFlow ? currentStep === summaryStep : currentStep === STEPS_LEGACY.SUMMARY;
  const isPhotoCheck = useProductFlow ? isProductPhotoStep(currentStep) : currentStep === STEPS_LEGACY.PHOTO_CHECK;
  const showNext =
    (useProductFlow && isProductDataStep(currentStep) && !canProceedFromProductData()) ||
    (!useProductFlow &&
      currentStep === STEPS_LEGACY.GATE_DATA &&
      !(
        gates[currentGateIndex] &&
        gates[currentGateIndex].widths.length >= 2 &&
        gates[currentGateIndex].heights.length >= 2 &&
        gates[currentGateIndex].surface.trim() !== ""
      ));

  const stepIndicator = (
    <StepIndicator
      currentStep={currentStep}
      totalSteps={totalSteps}
      stepLabels={stepLabels}
    />
  );

  const [mobileHeaderEl, setMobileHeaderEl] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    if (isMobile) setMobileHeaderEl(document.getElementById("measure-step-header"));
  }, [isMobile]);

  if (isMobile && mobileHeaderEl) {
    createPortal(stepIndicator, mobileHeaderEl);
  }

  return (
    <div className="h-full flex flex-col">
      {/* On mobile the step indicator is portaled into #measure-step-header; on desktop render here */}
      {!isMobile && stepIndicator}

      <div className={cn("flex-1 overflow-y-auto", isMobile && "pb-28")}>
        {currentStep === STEPS.DEAL_SELECT && (
          <div className="p-4 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t("measurement.selectDealTitle")}</CardTitle>
                <CardDescription>{t("measurement.selectDealForMeasurement")}</CardDescription>
              </CardHeader>
              <CardContent>
                <Popover open={dealSelectOpen} onOpenChange={setDealSelectOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" role="combobox" className="w-full justify-between">
                      {dealId && (deal.data?.title || dealTitleFromMeasureList)
                        ? String(deal.data?.title || dealTitleFromMeasureList)
                        : t("measurement.selectDealPlaceholder")}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] p-0"
                    align="start"
                    side="bottom"
                    avoidCollisions={false}
                  >
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder={t("measurement.searchDealsPlaceholder")}
                        value={dealSearchValue}
                        onValueChange={setDealSearchValue}
                        autoFocus={false}
                      />
                      <CommandList>
                        <CommandEmpty>{t("common.noResults")}</CommandEmpty>
                        {filteredGroups.map((group) => (
                          <CommandGroup key={group.label} heading={group.label}>
                            {group.deals.map((d: any) => (
                              <CommandItem
                                key={d.id}
                                value={`${group.label}-${d.id}-${d.title || d.id}`}
                                onSelect={() => handleDealSelect(d.id)}
                              >
                                <Check className={cn("mr-2 h-4 w-4", dealId === d.id ? "opacity-100" : "opacity-0")} />
                                <span className="flex-1 truncate">{d.title || d.id}</span>
                                {hasDraftData(d) && (
                                  <span className="shrink-0 text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded">
                                    {t("measurementExtra.resumeDraft")}
                                  </span>
                                )}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        ))}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
                <div className="flex gap-2 mt-3">
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1"
                    onClick={() => measureDeals.refetch()}
                    disabled={measureDeals.isFetching}
                  >
                    {measureDeals.isFetching ? t("common.loading") : t("common.refresh")}
                  </Button>
                  {onCreateDeal && (
                    <Button type="button" variant="outline" className="flex-1" onClick={onCreateDeal}>
                      {t("measure.newDeal")}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {useProductFlow && currentStep === STEPS.PRODUCT_TYPES && (
          <div className="p-4 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t("measurementExtra.productTypesTitle")}</CardTitle>
                <CardDescription>{t("measurementExtra.productTypesDesc")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">{t("measurementExtra.quickTemplates")}</p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { label: "2 Garázskapu + 1 Automatizáció", types: [{ name: "Garázskapu", count: 2 }, { name: "Tóló kapu automatizáció", count: 1 }] },
                      { label: "1 Garázskapu", types: [{ name: "Garázskapu", count: 1 }] },
                      { label: "2 Garázskapu", types: [{ name: "Garázskapu", count: 2 }] },
                      { label: "1 Toló kapu + 1 Automatizáció", types: [{ name: "Toló kapu", count: 1 }, { name: "Tóló kapu automatizáció", count: 1 }] },
                      { label: "1 Klíma", types: [{ name: "Klíma", count: 1 }] },
                    ].filter((tpl) => tpl.types.every((t) => productTypes.some((pt) => pt.name === t.name)))
                    .map((tpl) => (
                        <Button
                          key={tpl.label}
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const names = tpl.types.map((t) => t.name);
                            setSelectedProductTypes(names);
                            const counts: Record<string, number> = {};
                            tpl.types.forEach((t) => { counts[t.name] = t.count; });
                            setCountByType((prev) => ({ ...prev, ...counts }));
                          }}
                        >
                          {tpl.label}
                        </Button>
                      ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-muted-foreground">{t("measurementExtra.orSelectIndividually")}</p>
                  {productTypes.map((pt) => (
                    <label
                      key={pt.id}
                      className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-muted/50"
                    >
                      <input
                        type="checkbox"
                        checked={selectedProductTypes.includes(pt.name)}
                        onChange={() => toggleProductType(pt.name)}
                        className="h-4 w-4"
                      />
                      <span className="font-medium">{pt.name}</span>
                    </label>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {useProductFlow && currentStep === STEPS.COUNT_PER_TYPE && (
          <div className="p-4 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t("measurementExtra.countPerTypeTitle")}</CardTitle>
                <CardDescription>{t("measurementExtra.countPerTypeDesc")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {selectedProductTypes.map((typeName) => {
                  const count = countByType[typeName] ?? 1;
                  return (
                    <div key={typeName} className="flex items-center justify-between gap-4">
                      <span className="font-medium">{typeName}</span>
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          min={1}
                          max={20}
                          value={count}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => handleCountChange(typeName, parseInt(e.target.value, 10) || 1)}
                          className="w-24"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 shrink-0"
                          onClick={() => handleCountChange(typeName, Math.min(20, count + 1))}
                          aria-label={t("common.increment")}
                        >
                          <Plus className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 shrink-0"
                          onClick={() => handleCountChange(typeName, Math.max(1, count - 1))}
                          aria-label={t("common.decrement")}
                        >
                          <Minus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          </div>
        )}

        {useProductFlow && isProductDataStep(currentStep) && productTypeForCurrent && currentProduct && (
          <div>
            <div className="p-4 border-b bg-muted/50">
              <span className="text-sm font-medium">
                {t("measurementExtra.productDataBanner", {
                  type: currentProduct.productType,
                  index: currentProduct.index,
                  phase: t("measurementExtra.shortMeasure"),
                })}
              </span>
            </div>
            <ProductItemForm
              productType={productTypeForCurrent}
              item={currentProduct}
              itemLabel={`${currentProduct.productType} ${currentProduct.index}`}
              onChange={handleProductChange}
            />
          </div>
        )}

        {!useProductFlow && currentStep === STEPS_LEGACY.GATE_COUNT && (
          <div className="p-4 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>{t("measurementExtra.gateCountTitle")}</CardTitle>
                <CardDescription>{t("measurementExtra.gateCountDesc")}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-4 gap-2">
                  {[1, 2, 3, 4].map((count) => (
                    <Button
                      key={count}
                      type="button"
                      variant={gateCount === count ? "default" : "outline"}
                      onClick={() => {
                        setGateCount(count);
                        setCurrentStep(STEPS_LEGACY.GATE_DATA);
                        setCurrentGateIndex(0);
                      }}
                      className="h-16 text-lg"
                    >
                      {count}
                    </Button>
                  ))}
                </div>
                <Input
                  type="number"
                  min={1}
                  max={10}
                  placeholder={t("measurement.enterNumberPlaceholder")}
                  value={gateCount > 4 ? gateCount : ""}
                  onChange={(e) => {
                    const val = parseInt(e.target.value, 10);
                    if (val > 0 && val <= 10) {
                      setGateCount(val);
                      setCurrentStep(STEPS_LEGACY.GATE_DATA);
                      setCurrentGateIndex(0);
                    }
                  }}
                  className="mt-4"
                />
              </CardContent>
            </Card>
          </div>
        )}

        {!useProductFlow && currentStep === STEPS_LEGACY.GATE_DATA && (
          <div>
            <div className="p-4 border-b bg-muted/50">
              <span className="text-sm font-medium">
                {t("measurementExtra.gateProgress", { current: currentGateIndex + 1, total: gateCount })}
              </span>
            </div>
            <GateMeasurementForm
              gate={
                gates[currentGateIndex] || {
                  id: `gate-${Date.now()}`,
                  widths: [],
                  heights: [],
                  notes: "",
                  surface: "",
                  color: "",
                  prelis: "",
                }
              }
              gateIndex={currentGateIndex}
              onChange={(gate) => {
                const next = [...gates];
                next[currentGateIndex] = gate;
                setGates(next);
              }}
            />
          </div>
        )}

        {isPhotoCheck && dealId && (
          <PhotoCheckStep
            dealId={dealId}
            productLabel={
              useProductFlow && isProductPhotoStep(currentStep)
                ? (() => {
                    const idx = productIndexFromStep(currentStep);
                    const p = flatProducts[idx];
                    return p ? `${p.productType} ${p.index}` : undefined;
                  })()
                : undefined
            }
          />
        )}

        {isSummary && (
          <SummaryStep
            gates={useProductFlow ? undefined : gates}
            products={useProductFlow ? flatProducts : undefined}
            onEditGate={!useProductFlow ? (idx) => { setCurrentGateIndex(idx); setCurrentStep(STEPS_LEGACY.GATE_DATA); } : undefined}
            onEditProduct={useProductFlow ? (idx) => setCurrentStep(STEPS.FIRST_PRODUCT + 2 * idx) : undefined}
            onEditProductPhotos={useProductFlow ? (idx) => setCurrentStep(STEPS.FIRST_PRODUCT + 2 * idx + 1) : undefined}
          />
        )}
      </div>

      <div className={cn(
        "border-t bg-background p-4 flex gap-2",
        isMobile && "fixed left-0 right-0 z-10 shadow-[0_-2px_8px_rgba(0,0,0,0.15)]",
        isMobile && "bottom-[calc(4rem+env(safe-area-inset-bottom))]" // Above bottom nav + safe area
      )}>
        {currentStep > 1 && !isSummary && (
          <Button type="button" variant="outline" onClick={goPrev} className={cn("flex-1", isMobile && "min-h-12 text-base")}>
            <ArrowLeft className={cn("h-4 w-4 mr-2", isMobile && "h-5 w-5")} />
            {t("common.back")}
          </Button>
        )}
        {isSummary && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setCurrentStep(useProductFlow ? summaryStep - 1 : STEPS_LEGACY.PHOTO_CHECK)}
            className={cn("flex-1", isMobile && "min-h-12 text-base")}
          >
            <ArrowLeft className={cn("h-4 w-4 mr-2", isMobile && "h-5 w-5")} />
            {t("common.back")}
          </Button>
        )}
        {!isSummary && (
          <Button
            type="button"
            onClick={goNext}
            disabled={
              (currentStep === 1 && !dealId) ||
              (useProductFlow && currentStep === STEPS.PRODUCT_TYPES && selectedProductTypes.length === 0) ||
              showNext
            }
            className={cn("flex-1", isMobile && "min-h-12 text-base")}
          >
            {t("measurementExtra.wizardContinue")}
            <ArrowRight className={cn("h-4 w-4 ml-2", isMobile && "h-5 w-5")} />
          </Button>
        )}
        {isSummary && onComplete && (
          <Button type="button" onClick={() => {
            const md: MeasurementData = useProductFlow
              ? { products: flatProducts, gates: [] }
              : { gates };
            onComplete(md);
          }} className="flex-1" size="lg">
            {t("measurementExtra.wizardComplete")}
          </Button>
        )}
      </div>
    </div>
  );
}
